import parseSvg from 'html-react-parser/lib';
import svgPath from 'svgpath';
import { v4 as uuid } from 'uuid';
import { formatSvgPathDecimals } from '@/utils/file-kit';
import { type TextFragment } from '@/utils/text-kit';
import { type IAdvancedTextMaterial, type ILayerProps, type ITextContent, type ITextLayerProps } from '@/version-types';

type EnhancedFragment = TextFragment & {
    originIdx: number;
};

/**
 * 根据花字创建 textContents
 * 在内容输入时，若该图层使用了花字，则需要将文本逐字拆分成 textContent 成员
 *
 * @param text - 文本
 * @param layerData - 文本图层数据
 * @returns textContents
 */
export const createTextContentsByArtText = (text: string, layerData: ITextLayerProps) => {
    const { textContents = [], advancedTextMaterial, textMark } = layerData;
    if (!advancedTextMaterial) return textContents;

    const primaryFontFamily = layerData.fontFamily;
    const primaryFontSize = layerData.fontSize;

    const ret: ITextContent[] = [];
    const segmentIterator = new Intl.Segmenter().segment(text);

    // [bugfix]: https://jira.meitu.com/browse/MTXXPRO-64325
    let shouldAttachTextMark = false;
    if (textMark) {
        if (textContents.length === 0) {
            shouldAttachTextMark = true;
        } else {
            const hasNoScopedTextMark = textContents.every((item) => !item.textMark);
            if (hasNoScopedTextMark) {
                shouldAttachTextMark = true;
            }
        }
    }

    let idx = 0;

    for (const segment of segmentIterator) {
        const char = segment.segment;
        if (char === '\n') {
            const prevTextContent = ret[idx - 1];
            if (prevTextContent) prevTextContent.text += '\n';
            continue;
        }

        const textContentPreset = (() => {
            if (advancedTextMaterial.material.useCount) {
                const targetIdx = idx % advancedTextMaterial.material.useCount;
                return advancedTextMaterial.textContents[targetIdx];
            }
            return advancedTextMaterial.textContents[0];
        })();

        const nextContent: ITextContent = {
            ...textContentPreset,
            text: segment.segment,
            fontSize: primaryFontSize,
            fontFamily: primaryFontFamily, // 理论上，应用了花字后，花字文本的 fontFamily 会和 layer 的 fontFamily 一致，所以这里直接使用 layer 的 fontFamily，防止用户有切换过字体
        };

        if (shouldAttachTextMark) {
            nextContent.textMark = textMark;
        }

        ret.push(nextContent);
        idx++;
    }

    return ret;
};

/**
 * ## 花字文档
 *
 * https://cf.meitu.com/confluence/pages/viewpage.action?pageId=561872213
 *
 * ## 花字资源（SVG）规则：
 *
 * 1. SVG 的内容主要由 path 组成，存在使用 g 标签包裹 path 的情况
 * 2. 每个 path 大小一致，viewBox 宽度为 path 个数的总和
 * 3. 素材类型：
 *  - stretch: 长度拉伸;
 *  - cover: 拉伸填充;
 *  - use: 使用 use，需要对 svg 预先处理，参考三角形，use里如果有多段path，按照path个数来重复
 *  - repeat: 重复;
 *  - combined: 拼接;
 *  - custom: 自定义类型
 */

export class ArtTextSVGCreator {
    private constructor() {
        throw new Error('ArtTextSVGCreator is a static class');
    }

    private static layerData: ITextLayerProps;
    private static config: IAdvancedTextMaterial;

    /**
     * 按行分组的文本片段
     */
    private static groupedFragments: EnhancedFragment[][] = [];

    /**
     * 字体缩放比例
     */
    private static ratio = 1;

    /**
     * 原始 svg 根元素
     */
    private static svgRoot: HTMLElement | null = null;

    /**
     * 是否为组路径
     */
    private static isGroupPath = false;

    /**
     * 原始 svg 宽度
     */
    private static width = 0;

    /**
     * 原始 svg 高度
     */
    private static height = 0;

    /**
     * 初始化 SVG 根元素信息
     *
     * @param svgContent - SVG 原始字符串
     */
    private static initSVGRootElementInfo(svgContent: string) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgElement = doc.documentElement;

        const viewBox = svgElement.getAttribute('viewBox');
        if (!viewBox) return;

        this.svgRoot = svgElement;
        this.isGroupPath = svgElement.querySelector('g') != null;

        const [width, height] = viewBox.split(' ').slice(-2).map(Number);
        this.width = width;
        this.height = height;
    }

    /**
     * 按行对文本片段进行分组
     *
     * @param fragments - 文本片段
     */
    private static initGroupedFragments(fragments: TextFragment[]) {
        const groupedFragments: EnhancedFragment[][] = [];
        let currentGroup: EnhancedFragment[] = [];

        for (let i = 0; i < fragments.length; i++) {
            const fragment = fragments[i];
            if (currentGroup.length === 0 || fragment.line === currentGroup[0].line) {
                currentGroup.push({ ...fragment, originIdx: i });
            } else {
                groupedFragments.push(currentGroup);
                currentGroup = [{ ...fragment, originIdx: i }];
            }
        }

        groupedFragments.push(currentGroup);

        this.groupedFragments = groupedFragments;
    }

    /**
     * 对花字素材 SVG 进行预处理
     *
     * @param svgElement - SVG 元素
     * @returns - 预处理后的 SVG 元素
     */
    private static preprocessSVG(svgElement: SVGElement): SVGElement | null {
        const paths = svgElement.querySelectorAll('path');
        if (paths.length === 0) return null;

        // 重置 g 标签 id
        if (this.isGroupPath) {
            const gElements = svgElement.querySelectorAll('g');
            gElements.forEach((gElement) => {
                const id = uuid();
                gElement.setAttribute('id', id);
            });
        }

        // 缩放路径、重置 path id
        paths.forEach((path) => {
            let d = path.getAttribute('d') as string;
            d = formatSvgPathDecimals(svgPath(d).scale(this.ratio).toString());
            path.setAttribute('d', d);

            // 设计师要求描边宽度也要等比缩放
            const strokeWidth = path.getAttribute('stroke-width');
            if (strokeWidth) {
                path.setAttribute('stroke-width', `${Number(strokeWidth) * this.ratio}`);
            }

            if (!this.isGroupPath) {
                const id = uuid();
                path.setAttribute('id', id);
            }
        });

        // 删除 use 元素
        const uses = svgElement.querySelectorAll('use');
        if (uses.length > 0) uses.forEach((use) => use.remove());

        // 设置基础样式
        // TODO: 是否需要再根据花字配置中的 offset/padding 数据设置 svg 的位置，目前看来已有的效果暂时不需要设置
        svgElement.style.position = 'absolute';
        svgElement.style.overflow = 'visible';
        svgElement.style.zIndex = '-1';

        return svgElement;
    }

    private static createBackgroundSVG() {
        if (!this.svgRoot || this.groupedFragments.length === 0) return null;

        const preprocessSVG = this.preprocessSVG(this.svgRoot.cloneNode(true) as SVGElement);
        if (!preprocessSVG) return null;

        const shapes = (() => {
            if (this.isGroupPath) return preprocessSVG.querySelectorAll('g');
            return preprocessSVG.querySelectorAll('path');
        })();

        const { material } = this.config;
        const { useCount } = material;

        // 检查路径数量是否匹配
        if (useCount) {
            if (shapes.length !== useCount) return null;
        }

        const unitWidth = (useCount ? this.width / useCount : this.width) * this.ratio;
        const unitHeight = this.height * this.ratio;

        // 根据行数据生产 svg
        const svgElements: SVGElement[] = [];

        for (let line = 0; line < this.groupedFragments.length; line++) {
            const fragments = this.groupedFragments[line];

            const totalWidth = fragments.reduce((acc, fragment) => acc + fragment.width, 0);
            const totalHeight = Number((this.height * this.ratio).toFixed(1));

            const svgElement = preprocessSVG.cloneNode(true) as SVGElement;
            svgElement.setAttribute('width', `${totalWidth}px`);
            svgElement.setAttribute('height', `${totalHeight}px`);
            svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);

            fragments.forEach((fragment) => {
                const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
                const hrefPathIdx = useCount ? fragment.originIdx % shapes.length : 0;
                const hrefPath = shapes[hrefPathIdx];
                const hrefId = hrefPath.getAttribute('id') as string;
                use.setAttribute('xlink:href', `#${hrefId}`);

                const charWidth = fragment.width - this.layerData.letterSpacing;
                const x = fragment.left + (charWidth - unitWidth) / 2;

                const charHeight = fragment.height;
                const y = fragment.top + (charHeight - unitHeight) / 2;

                use.setAttribute('x', `${x}`);
                use.setAttribute('y', `${y}`);

                svgElement.appendChild(use);
            });

            svgElements.push(svgElement);
        }

        const serializer = new XMLSerializer();
        const result = svgElements.map((svgElement) => {
            const svgString = serializer.serializeToString(svgElement);
            return parseSvg(svgString);
        });

        return result;
    }

    static create(layer: ILayerProps, fragments: TextFragment[], svgContent: string) {
        if (fragments.length === 0 || !svgContent) return null;

        const layerData = layer.data as ITextLayerProps;
        const config = layerData.advancedTextMaterial as IAdvancedTextMaterial;

        const { material } = config;
        const { defaultFontSize } = material;

        this.layerData = layerData;
        this.config = config;
        this.ratio = layerData.fontSize / defaultFontSize;

        this.initGroupedFragments(fragments);
        this.initSVGRootElementInfo(svgContent);

        return this.createBackgroundSVG();
    }
}
